import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchCustomersForSelect, getPromissoryNotes } from '@/lib/supabaseDb';
import { useDebounce } from '@/hooks/useDebounce';
import { toTurkishLower, normalizePhoneForSearch } from '@/lib/customerSearch';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import Badge from '@/components/base/Badge';
import { installmentStatus } from '@/lib/statusDefs';

function formatTRY(amount: number): string {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);
}

function getCustomerDebtBadge(debt: number): string {
  if (debt <= 0) return 'text-emerald-600';
  return 'text-red-500';
}

interface CustomerResult {
  type: 'customer';
  id: string;
  customer_no: string;
  full_name: string;
  phone: string;
  total_debt_amount: number;
  status: string;
}

interface NoteResult {
  type: 'note';
  id: string;
  note_no: string;
  customer_name: string;
  customer_id: string;
  amount: number;
  note_book_no: string;
  note_page_no: string;
  due_date: string;
  status: string;
}

type SearchResult = CustomerResult | NoteResult;

export default function GlobalSearch() {
  const navigate = useNavigate();
  const { version } = useDataRefresh();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [customers, setCustomers] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  // İlk yükleme: tüm müşteriler ve senetler
  useEffect(() => {
    async function load() {
      const [custRes, notesRes] = await Promise.all([searchCustomersForSelect(''), getPromissoryNotes()]);
      setCustomers(custRes.data || []);
      setNotes(notesRes.data || []);
    }
    load();
  }, [version]);

  // Debounced server-side müşteri araması
  const debouncedQuery = useDebounce(query.trim(), 300);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) return;
    let cancelled = false;
    setSearching(true);
    searchCustomersForSelect(debouncedQuery).then(({ data }) => {
      if (cancelled) return;
      setCustomers(data || []);
      setSearching(false);
    }).catch(() => {
      if (!cancelled) setSearching(false);
    });
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const customerNoMap = useMemo(() => {
    const map: Record<string, string> = {};
    customers.forEach((c: any) => { map[c.id] = c.customer_no || ''; });
    return map;
  }, [customers]);

  const results = useMemo((): SearchResult[] => {
    if (!query.trim()) return [];
    const q = toTurkishLower(query.trim());
    const qPhone = normalizePhoneForSearch(query);
    const maxResults = 8;

    const customerResults: CustomerResult[] = [];
    const noteResults: NoteResult[] = [];

    for (const c of customers) {
      const match =
        toTurkishLower(c.full_name || '').includes(q) ||
        toTurkishLower(c.customer_no || '').includes(q) ||
        (qPhone !== '' && normalizePhoneForSearch(c.phone || '').includes(qPhone)) ||
        (qPhone !== '' && normalizePhoneForSearch(c.second_phone || '').includes(qPhone)) ||
        (c.identity_number || '').includes(q);
      if (match) {
        customerResults.push({ type: 'customer', id: c.id, customer_no: c.customer_no || '', full_name: c.full_name || '', phone: c.phone || '', total_debt_amount: c.total_debt_amount || 0, status: c.status || 'Aktif' });
        if (customerResults.length >= maxResults) break;
      }
    }

    for (const n of notes) {
      const match =
        toTurkishLower(n.note_no || '').includes(q) ||
        toTurkishLower(n.customer_name || '').includes(q) ||
        toTurkishLower(customerNoMap[n.customer_id] || '').includes(q) ||
        (qPhone !== '' && normalizePhoneForSearch(n.customer_phone || '').includes(qPhone)) ||
        toTurkishLower(n.note_book_no || '').includes(q) ||
        toTurkishLower(n.note_page_no || '').includes(q);
      if (match) {
        noteResults.push({ type: 'note', id: n.id, note_no: n.note_no || '', customer_name: n.customer_name || '', customer_id: n.customer_id || '', amount: n.amount || 0, note_book_no: n.note_book_no || '', note_page_no: n.note_page_no || '', due_date: n.due_date || '', status: n.status || 'Bekliyor' });
        if (noteResults.length >= maxResults) break;
      }
    }

    const combined: SearchResult[] = [];
    const cLimit = Math.min(customerResults.length, 4);
    const nLimit = Math.min(noteResults.length, 4);
    for (let i = 0; i < cLimit; i++) combined.push(customerResults[i]);
    for (let i = 0; i < nLimit; i++) combined.push(noteResults[i]);
    return combined;
  }, [query, customers, notes, customerNoMap]);

  const customerGroup = results.filter((r): r is CustomerResult => r.type === 'customer');
  const noteGroup = results.filter((r): r is NoteResult => r.type === 'note');
  const allResults = [...customerGroup, ...noteGroup];

  useEffect(() => { setSelectedIndex(-1); }, [query]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(result: SearchResult) {
    setIsOpen(false);
    setQuery('');
    if (result.type === 'customer') navigate(`/customers/${result.id}`);
    else navigate(`/promissory-notes?highlight=${result.id}`);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen || allResults.length === 0) {
      if (e.key === 'Enter' && query.trim()) setIsOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (allResults.length === 1) handleSelect(allResults[0]); else if (selectedIndex >= 0 && selectedIndex < allResults.length) handleSelect(allResults[selectedIndex]); }
    else if (e.key === 'Escape') { setIsOpen(false); inputRef.current?.blur(); }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setQuery(e.target.value);
    if (e.target.value.trim()) setIsOpen(true);
    else setIsOpen(false);
  }

  return (
    <div className="relative hidden sm:block" ref={containerRef}>
      <input ref={inputRef} type="text" placeholder="Müşteri, telefon, müşteri no veya senet no ara..." value={query} onChange={handleInputChange} onKeyDown={handleKeyDown} onFocus={() => { if (query.trim()) setIsOpen(true); }} className="w-72 pl-9 pr-3 py-2 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-700 placeholder:text-foreground-300 transition-all" />
      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-[480px] max-w-[calc(100vw-2rem)] bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 z-50 overflow-hidden">
          {allResults.length === 0 && query.trim() && (
            <div className="px-4 py-8 text-center">
              <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3"><i className="ri-search-line text-xl text-foreground-300"></i></div>
              <p className="text-sm font-medium text-foreground-500">Sonuç bulunamadı</p>
              <p className="text-xs text-foreground-400 mt-0.5">Farklı anahtar kelimelerle tekrar deneyin</p>
            </div>
          )}

          {customerGroup.length > 0 && (
            <div>
              <div className="px-4 py-1.5"><p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest">Müşteriler</p></div>
              {customerGroup.map((result, idx) => {
                const globalIdx = allResults.indexOf(result);
                return (
                  <button key={`cust-${result.id}`} onClick={() => handleSelect(result)} onMouseEnter={() => setSelectedIndex(globalIdx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${selectedIndex === globalIdx ? 'bg-background-100' : 'hover:bg-background-50'}`}>
                    <span className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0"><i className="ri-user-3-line text-primary-600 text-sm"></i></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="text-sm font-medium text-foreground-800 truncate">{result.full_name}</span><span className="text-[10px] text-foreground-400 font-mono flex-shrink-0">{result.customer_no}</span></div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-foreground-400">{result.phone}</span>
                        <span className={`text-xs font-medium ${getCustomerDebtBadge(result.total_debt_amount)}`}>{formatTRY(result.total_debt_amount)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${result.status === 'Aktif' ? 'bg-emerald-100 text-emerald-700' : result.status === 'Borçlu' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{result.status}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0"></i>
                  </button>
                );
              })}
            </div>
          )}

          {noteGroup.length > 0 && (
            <div>
              {customerGroup.length > 0 && <div className="mx-4 border-t border-background-100 my-1"></div>}
              <div className="px-4 py-1.5"><p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-widest">Senetler</p></div>
              {noteGroup.map((result, idx) => {
                const globalIdx = allResults.indexOf(result);
                return (
                  <button key={`note-${result.id}`} onClick={() => handleSelect(result)} onMouseEnter={() => setSelectedIndex(globalIdx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${selectedIndex === globalIdx ? 'bg-background-100' : 'hover:bg-background-50'}`}>
                    <span className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0"><i className="ri-file-list-3-line text-accent-700 text-sm"></i></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="text-sm font-medium text-foreground-800 truncate">{result.note_no}</span><span className="text-xs text-foreground-500 truncate">{result.customer_name}</span></div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs font-medium text-foreground-700">{formatTRY(result.amount)}</span>
                        <span className="text-xs text-foreground-400">{result.note_book_no} / Sayfa {result.note_page_no}</span>
                        <span className="text-[10px] text-foreground-400">{result.due_date ? new Date(result.due_date).toLocaleDateString('tr-TR') : ''}</span>
                        <Badge badgeClass={`${installmentStatus.badgeClass(result.status)} text-[10px]`} icon={installmentStatus.icon(result.status)}>{result.status}</Badge>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0"></i>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}